import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, formatDate, money } from "@/lib/api";
import { PARTNER_NAV } from "./nav";

const STATUS = {
  active: { label: "Active", className: "bg-emerald-100 text-emerald-900" },
  complimentary: { label: "Complimentary", className: "bg-blue-100 text-blue-900" },
  grace: { label: "Past due — grace period", className: "bg-amber-100 text-amber-900" },
  unpaid: { label: "Payment required", className: "bg-amber-100 text-amber-900" },
  locked: { label: "Locked", className: "bg-red-100 text-red-900" },
  pending_deletion: { label: "Locked", className: "bg-red-100 text-red-900" },
};

export default function PartnerPlan() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [inquiry, setInquiry] = useState({ student_count: "", note: "", email: "" });
  const [sending, setSending] = useState(false);
  const [listing, setListing] = useState({ website_url: "", public_description: "" });
  const [savingListing, setSavingListing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/partner/subscription");
      setData(res.data);
      setInquiry((i) => ({ ...i, email: i.email || res.data.contact_email || "" }));
      setListing({
        website_url: res.data.website_url || "",
        public_description: res.data.public_description || "",
      });
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const choose = async (planCode) => {
    setBusy(planCode);
    try {
      const { data: res } = await api.post("/partner/subscription/checkout", {
        plan_code: planCode,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy("");
    }
  };

  const askEnterprise = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/partner/subscription/enterprise-inquiry", {
        organization: data.partner_name,
        contact_name: data.contact_name || data.partner_name,
        email: inquiry.email,
        student_count: Number(inquiry.student_count) || 0,
        note: inquiry.note,
      });
      toast.success(res.message);
      setInquiry({ ...inquiry, student_count: "", note: "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const saveListing = async () => {
    setSavingListing(true);
    try {
      const { data: res } = await api.put("/partner/profile", listing);
      setListing(res);
      toast.success("Public listing updated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingListing(false);
    }
  };

  if (!data)
    return (
      <AppShell nav={PARTNER_NAV} title="Plan & billing">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </AppShell>
    );

  const status = STATUS[data.status] || STATUS.unpaid;
  const pct = Math.min(100, data.usage_pct);

  return (
    <AppShell
      nav={PARTNER_NAV}
      title="Plan & billing"
      subtitle="Your annual Alabama Church School partner registration"
    >
      <div className="space-y-8">
        {data.status === "locked" || data.status === "pending_deletion" ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="flex items-start gap-3 p-5">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
              <div className="text-sm text-red-900" data-testid="plan-locked-notice">
                <strong>Your registration has lapsed.</strong> Uploading records and adding
                students is paused. Your existing student records are safe — renew below to
                restore access.
              </div>
            </CardContent>
          </Card>
        ) : data.status === "grace" ? (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="flex items-start gap-3 p-5">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
              <div className="text-sm text-amber-900" data-testid="plan-grace-notice">
                Your registration is past due. You have a {data.grace_days}-day grace period before
                uploads are paused.
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Current plan
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <span className="font-display text-2xl font-bold text-slate-900" data-testid="current-plan-name">
                    {data.plan_name}
                  </span>
                  <Badge className={status.className} data-testid="plan-status-badge">
                    {status.label}
                  </Badge>
                </div>
                <div className="mt-2 text-sm text-slate-600" data-testid="plan-renewal-info">
                  {data.comped
                    ? "Complimentary — no payment required."
                    : data.paid_through
                    ? data.days_left >= 0
                      ? `Renews ${formatDate(data.paid_through)} · ${data.days_left} days left`
                      : `Was due ${formatDate(data.paid_through)}`
                    : "Awaiting first payment"}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-slate-900">
                  {money(data.plan_price)}
                </div>
                <div className="text-xs text-slate-500">per year</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-slate-700">Active students</span>
                <span className="text-slate-900" data-testid="plan-usage-text">
                  {data.active_students} of {data.student_limit}
                </span>
              </div>
              <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.at_limit ? "bg-red-500" : data.near_limit ? "bg-amber-500" : "bg-emerald-500"
                  }`}
                  style={{ width: `${pct}%` }}
                  data-testid="plan-usage-bar"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Archived students stay accessible and never count toward your limit.
                {data.archived_students > 0 && ` ${data.archived_students} archived.`}
              </p>
              {data.at_limit && (
                <p className="mt-2 text-sm font-medium text-red-700" data-testid="plan-at-limit">
                  You have used every active student place. Upgrade to add more — nothing is
                  deleted.
                </p>
              )}
              {data.near_limit && (
                <p className="mt-2 text-sm font-medium text-amber-700" data-testid="plan-near-limit">
                  You are approaching your limit ({data.seats_left} {data.seats_left === 1 ? "place" : "places"} left). Consider
                  upgrading soon.
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div>
          <h2 className="text-base font-semibold text-slate-900">Plans</h2>
          <p className="mb-4 mt-1 text-sm text-slate-600">
            Upgrading mid-year costs only the difference for the remainder of your year, and your
            renewal date stays the same.
          </p>
          <div className="grid gap-4 lg:grid-cols-3">
            {data.plans.map((p) => {
              const current = p.code === data.plan_code;
              return (
                <Card
                  key={p.code}
                  className={current ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      {current && (
                        <Badge className="bg-slate-900 text-white">
                          <CheckCircle2 className="mr-1 h-3 w-3" /> Current
                        </Badge>
                      )}
                    </div>
                    <div className="mt-3 font-display text-3xl font-bold text-slate-900">
                      {money(p.price)}
                    </div>
                    <div className="text-xs text-slate-500">per year</div>
                    <div className="mt-3 text-sm text-slate-700">
                      Up to {p.student_limit.toLocaleString()} active students
                    </div>
                    <Button
                      className="mt-5 w-full gap-2"
                      variant={current ? "outline" : "default"}
                      disabled={!!busy || (current && data.status === "active")}
                      onClick={() => choose(p.code)}
                      data-testid={`choose-plan-${p.code}`}
                    >
                      {busy === p.code ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowUpRight className="h-4 w-4" />
                      )}
                      {current ? "Renew this plan" : "Switch to this plan"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="space-y-4 p-6">
            <h2 className="text-base font-semibold text-slate-900">Your public listing</h2>
            <p className="text-sm text-slate-600">
              Families who arrive at Alabama Church School without a partner can find you on the
              participating partners page. Add your website and a short description so they know
              what you offer.
            </p>
            <div className="space-y-2">
              <Label>Your website</Label>
              <Input
                placeholder="yourschool.com"
                value={listing.website_url}
                onChange={(e) => setListing((l) => ({ ...l, website_url: e.target.value }))}
                data-testid="listing-website"
              />
            </div>
            <div className="space-y-2">
              <Label>Short description (up to 400 characters)</Label>
              <Textarea
                rows={3}
                maxLength={400}
                value={listing.public_description}
                onChange={(e) => setListing((l) => ({ ...l, public_description: e.target.value }))}
                data-testid="listing-description"
              />
            </div>
            <Button
              className="gap-2"
              onClick={saveListing}
              disabled={savingListing}
              data-testid="save-listing"
            >
              {savingListing && <Loader2 className="h-4 w-4 animate-spin" />}
              Save listing
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="space-y-4 p-6">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-500" />
              <h2 className="text-base font-semibold text-slate-900">
                More than {data.enterprise_above.toLocaleString()} students?
              </h2>
            </div>
            <p className="text-sm text-slate-600">
              Contact ACS for enterprise pricing. Tell us how many active students you have and we
              will get back to you.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Your email</Label>
                <Input
                  type="email"
                  value={inquiry.email}
                  onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })}
                  data-testid="enterprise-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Active students</Label>
                <Input
                  type="number"
                  value={inquiry.student_count}
                  onChange={(e) => setInquiry({ ...inquiry, student_count: e.target.value })}
                  data-testid="enterprise-student-count"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Anything we should know?</Label>
              <Textarea
                rows={3}
                value={inquiry.note}
                onChange={(e) => setInquiry({ ...inquiry, note: e.target.value })}
                data-testid="enterprise-note"
              />
            </div>
            <Button
              className="gap-2"
              onClick={askEnterprise}
              disabled={sending}
              data-testid="enterprise-inquiry-submit"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              Contact ACS for enterprise pricing
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
